import React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import flow from 'lodash/fp/flow';
import SquareButton from '../Buttons/SquareButton';
import ModalWithOverlay from './ModalWithOverlay';
import PreKittMessageFrame from './PreKittMessageFrame';
import { translate } from '../../../translations/translator';
import { closeLicensingModal } from 'state/modules/messaging';
import { remCalc } from '../../../util/style-util';
// import { getClickAwayResourcesLink } from '../../../util/workbook-util';
import { black, white } from '../../../styles/style-constants';
import config from 'config';
import alert from 'assets/images/alert.svg';
import clickAway from 'assets/images/a-click-away.svg';
import prekittDevices from 'assets/images/prekitt-devices.png';

import { UserSetting, AppStoreState, SETTING_TYPES } from 'types';
import { logoutUser } from 'state/modules/user/actions';
import { UserState } from 'state/modules/user';
import { getUser, saveUserSetting } from 'state/modules/user/thunks';
import { FONT_GSS_FUTURA } from 'themes/default';
import { makeStyles } from '@mui/styles';
import { hasClickAwayOnly } from 'state/modules/user/selectors/user-selectors';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
const translateModals = key => translate('modals', key);
const translateHome = key => translate('home', key);

type Props =
  | {
      saveUserSetting: (type: string, setting: UserSetting) => void;
      user: UserState;
      workbook: any;
    }
  | any;

const ExpiredLicenseModal = ({ user, saveUserSetting, workbook }: Props) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [, { closeModal }] = useModal();
  const dispatch = useDispatch() as any;

  const isClickAwayOnly = useSelector(state => hasClickAwayOnly(state));

  const handleGotoClickAway = async () => {
    const products = user.licenses.map(license => {
      return { productCurriculumId: license.productCurriculumId };
    });
    const suppressedExpirationAlerts = user.settings.suppressedExpirationAlerts || [];
    const suppressedProducts = workbook ? workbook?.products : products;
    closeModal();

    await saveUserSetting(SETTING_TYPES.SUPPRESSED_EXPIRATION_ALERTS, [
      ...suppressedExpirationAlerts,
      ...suppressedProducts
    ]);
    await dispatch(getUser());
  };

  const logout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const getExpirationInstructions = (hasClickAway: boolean) => {
    if (hasClickAway) {
      return (
        <p>
          {translateModals('licenseExpiredClickAway1')}
          <img src={clickAway} className={classes.clickAway} alt="A Click Away" />
          {translateModals('licenseExpiredClickAway2')}
        </p>
      );
    } else {
      return <h3 className={classes.bold}>{translateModals('learnMoreFromOurWebsite')}</h3>;
    }
  };

  const getActions = () => {
    const actions = [
      <li key="purchase" className={classes.actionListItem}>
        <a href={`${config.companyUrl}/idtt`}>
          <SquareButton text={translateModals('learnMore')} />
        </a>
      </li>
    ];

    if (isClickAwayOnly) {
      actions.push(
        <li key="clickaway" className={classes.actionListItem}>
          <div onClick={handleGotoClickAway} className={classes.pointer}>
            {translateModals('licenseNoThanksClickAway')}
          </div>
        </li>
      );
    } else {
      actions.push(
        <li key="selector" className={classes.actionListItem}>
          <div onClick={logout} className={classes.pointer}>
            {translateHome('logout')}
          </div>
        </li>
      );
    }

    return actions;
  };

  return (
    <ModalWithOverlay show>
      <PreKittMessageFrame classList={[classes.modalPositioning]}>
        <div className={classes.expiredLicenseModal}>
          <h2 className={classes.modalHeading}>
            <img src={alert} className={classes.alert} alt="Alert" />
            {translateModals('prekittExpired')}
          </h2>
          {getExpirationInstructions(isClickAwayOnly)}
          <div className={classes.featuresContainer}>
            <div className={classes.feature}>
              <img src={prekittDevices} className={classes.licenseImage} alt="PreKITT" />
            </div>
          </div>
          <ul id="actions" className={classes.actionList}>
            {getActions()}
          </ul>
        </div>
      </PreKittMessageFrame>
    </ModalWithOverlay>
  );
};

const useStyles = makeStyles(() => ({
  modalPositioning: {
    position: 'absolute',
    top: '25vh'
  },
  expiredLicenseModal: {
    textAlign: 'center',
    fontWeight: 500,
    maxWidth: remCalc(400),
    '@media(min-width: 1000px)': {
      maxWidth: remCalc(600)
    }
  },
  alert: {
    width: remCalc(40),
    padding: remCalc(0, 8, 4),
    '@media(min-width: 1000px)': {
      width: remCalc(50)
    }
  },
  clickAway: {
    width: remCalc(40),
    padding: remCalc(0, 2, 4, 8),
    '@media(min-width: 1000px)': {
      width: remCalc(50)
    },
    textAlign: 'center'
  },
  modalHeading: {
    backgroundColor: black,
    color: white,
    fontFamily: FONT_GSS_FUTURA,
    fontWeight: 600,
    fontSize: remCalc(24),
    padding: remCalc(5),
    margin: remCalc(0, 0, 10),
    '@media(min-width: 1000px)': {
      fontSize: remCalc(30)
    }
  },
  modalText: {
    textAlign: 'left'
  },
  featuresContainer: {
    display: 'flex'
  },
  feature: {
    flex: 1,
    padding: remCalc(15)
  },
  featureList: {
    padding: remCalc(10, 25)
  },
  licenseImage: {
    maxWidth: '100%'
  },
  bold: {
    fontWeight: 600
  },
  pointer: {
    cursor: 'pointer',
    color: '#337ab7',
    '&:hover': {
      color: '#26537B',
      textDecoration: 'underline'
    }
  },
  actionList: {
    listStyle: 'none'
  },
  actionListItem: {
    display: 'inline-block',
    padding: remCalc(0, 20)
  }
}));

const mapStateToProps = (state: AppStoreState) => ({
  workbook: state.app.currentWorkbook,
  user: state.user,
  entity: state.entity
});

const withRedux = connect(mapStateToProps, {
  saveUserSetting,
  closeLicensingModal
});

export default flow(withRedux)(ExpiredLicenseModal);
